/**
 * Assignment Attachments Service
 * Handles CRUD operations for attachments directly on assignments
 */

import { supabase } from '../lib/supabase'
import type { AssignmentAttachment } from '../types/assignmentAttachment'

const STORAGE_BUCKET = 'assignment-attachments'

const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

/**
 * Get all attachments for an assignment with signed URLs
 */
export async function getAttachments(assignmentId: string): Promise<AssignmentAttachment[]> {
  const { data, error } = await supabase
    .from('assignment_attachments')
    .select('*')
    .eq('assignment_id', assignmentId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching assignment attachments:', error)
    return []
  }

  // Generate signed URLs
  const attachmentsWithUrls = await Promise.all(
    (data || []).map(async (attachment) => {
      const { data: urlData } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(attachment.storage_path, 3600) // 1 hour expiry

      return {
        ...attachment,
        url: urlData?.signedUrl || ''
      }
    })
  )

  return attachmentsWithUrls as AssignmentAttachment[]
}

/**
 * Upload an attachment to an assignment
 */
export async function uploadAttachment(
  assignmentId: string,
  file: File
): Promise<{ success: boolean; attachment?: AssignmentAttachment; error?: string }> {
  try {
    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return { success: false, error: 'Filtypen stöds inte. Tillåtna: JPEG, PNG, GIF, WebP, PDF, DOC, DOCX, XLS, XLSX' }
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      return { success: false, error: 'Filen är för stor. Max 10 MB' }
    }

    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'Ej inloggad' }
    }

    // Get user profile for name
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('full_name')
      .eq('user_id', user.id)
      .single()

    if (!profile) {
      return { success: false, error: 'Kunde inte hämta användarprofil' }
    }

    // Create unique file path
    const fileExt = file.name.split('.').pop()
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
    const storagePath = `${assignmentId}/${fileName}`

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (uploadError) {
      console.error('Error uploading file:', uploadError)
      return { success: false, error: 'Kunde inte ladda upp fil' }
    }

    // Create attachment record
    const { data: attachment, error: dbError } = await supabase
      .from('assignment_attachments')
      .insert({
        assignment_id: assignmentId,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        storage_path: storagePath,
        uploaded_by: user.id,
        uploaded_by_name: profile.full_name || 'Okänd användare'
      })
      .select()
      .single()

    if (dbError) {
      // Clean up uploaded file
      await supabase.storage
        .from(STORAGE_BUCKET)
        .remove([storagePath])

      console.error('Error creating attachment record:', dbError)
      return { success: false, error: 'Kunde inte spara bilaga' }
    }

    // Get signed URL
    const { data: urlData } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(storagePath, 3600)

    return {
      success: true,
      attachment: {
        ...attachment,
        url: urlData?.signedUrl || ''
      }
    }
  } catch (error) {
    console.error('Error uploading attachment:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Okänt fel'
    }
  }
}

/**
 * Delete an attachment
 */
export async function deleteAttachment(
  attachmentId: string
): Promise<{ success: boolean; error?: string }> {
  // Get attachment to find storage path
  const { data: attachment } = await supabase
    .from('assignment_attachments')
    .select('storage_path')
    .eq('id', attachmentId)
    .single()

  if (!attachment) {
    return { success: false, error: 'Bilaga hittades inte' }
  }

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .remove([attachment.storage_path])

  if (storageError) {
    console.error('Error deleting file from storage:', storageError)
  }

  // Delete from database
  const { error } = await supabase
    .from('assignment_attachments')
    .delete()
    .eq('id', attachmentId)

  if (error) {
    console.error('Error deleting attachment:', error)
    return { success: false, error: 'Kunde inte ta bort bilaga' }
  }

  return { success: true }
}
